/**
 * Settings — vertical sticky sidebar nav + content panel pattern from the
 * SPEC.md §Settings section (line 261) and the SettingsV2 prototype at
 * handoff/prototype/v2/screens/detail-cost-settings.jsx.
 *
 * Sections in order (web/src/lib/settingsSections.ts, spec §4.1): Brand /
 * Posting / Autopilot / AI & credits / Security. The one-per-server controls
 * (YouTube cookies, residential proxy, source cache, limits & alerts, auto-scan
 * per brand) live on the Operator screen (`#/operator`, gated by
 * `QL_OPERATOR_PANEL`) — see web/src/screens/Operator.tsx. Everything that
 * was decorative (a fake voice prompt, a hard-coded price sheet, inert key
 * rows, read-only font tiles, a cron card) is deleted, not hidden.
 *
 * SchedulingCard holds real API wiring (connectScheduling/
 * disconnectScheduling/listSchedulingChannels) and stays byte-stable —
 * it's just slotted into the nav layout instead of the old 2-column grid.
 */
import {
  AlertTriangle,
  Bot,
  Calendar,
  Check,
  Cpu,
  Lock,
  Palette,
  Trash2,
} from "lucide-react";
import { renderSVG } from "uqr";
import { useCallback, useEffect, useState } from "react";
import { checkAuth, mfaDisable, mfaEnable, mfaSetup } from "../auth";
import { useIsMobile } from "../lib/useIsMobile";
import { visibleSections, type SectionId } from "../lib/settingsSections";
import {
  connectScheduling,
  disconnectScheduling,
  getSchedulingStatus,
  getSettings,
  getDefaultCaption,
  setDefaultCaption,
  resetDefaultCaption,
  getAutopilot,
  updateAutopilot,
  getCtaConfig,
  updateCtaConfig,
  createCta,
  deleteCta,
  ctaSlideUrl,
  listChannelsApi,
  listSchedulingChannels,
  restoreChannelApi,
  updateChannelApi,
  type Channel,
  type SchedulingChannel,
  type SchedulingStatus,
  type SettingsInfo,
  type PostPipelineId,
  type CtaConfig,
  type AutopilotStatus,
} from "../api";
import { AiProviderCard } from "../components/AiProviderCard";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { PageHeader } from "../components/PageHeader";
import { ServiceIcon, serviceLabel } from "../components/SocialIcons";
import {
  KV,
  LoadingCard,
  PillStatus,
  PillToggle,
  SettingCard,
  formatRelativeTime,
  v2Input,
} from "../components/settings/primitives";
import { cardStyle, ctaSmallStyle, inputStyle } from "../components/styles";
import { Select } from "../components/ui/Select";

/** Icons by section id — the list itself is pure (settingsSections.ts). */
const ICONS: Record<SectionId, typeof Palette> = {
  brand: Palette,
  posting: Calendar,
  autopilot: Bot,
  ai: Cpu,
  security: Lock,
};

interface SettingsProps {
  channelsEnabled?: boolean;
  /** The post-caption and CTA cards under Posting only when the active brand
   *  has posts. The Buffer card always shows — video scheduling needs it. */
  postsEnabled?: boolean;
  /** Open on a specific section. The Studio's credit chip passes "ai", so
   *  "Balance $0.00 → where do I fix that?" is one click rather than a hunt. */
  initialSection?: SectionId;
  /** Brand › Edit brand — lands in the edit wizard for that slug. */
  onEditBrand?: (slug: string) => void;
  /** Show the muted "Operator" link at the bottom (server feature flag). */
  operatorPanel?: boolean;
}

export function Settings({
  channelsEnabled,
  postsEnabled,
  initialSection,
  onEditBrand,
  operatorPanel,
}: SettingsProps = {}): React.JSX.Element {
  const isMobile = useIsMobile();
  const [active, setActive] = useState<SectionId>(initialSection ?? "brand");
  // Re-aim if the caller asks again while this screen is already mounted (the
  // credit chip is clickable from the Studio the user just came back from).
  useEffect(() => {
    if (initialSection) setActive(initialSection);
  }, [initialSection]);
  const [info, setInfo] = useState<SettingsInfo | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getSettings()
      .then(setInfo)
      .catch((err) => setError(err instanceof Error ? err.message : String(err)));
  }, []);

  const postsOn = postsEnabled ?? true;
  const sections = visibleSections({ postsEnabled: postsOn });

  return (
    <div>
      <PageHeader
        eyebrow="Settings"
        title="Your setup."
        subtitle="Your brand, posting, Autopilot, AI and security."
      />

      {error && (
        <div
          style={{
            ...cardStyle,
            padding: "12px 16px",
            marginBottom: 16,
            color: "var(--ql-danger)",
            borderColor: "rgba(248,113,113,.30)",
            background: "rgba(248,113,113,.06)",
          }}
        >
          {error}
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "240px 1fr", gap: 24 }}>
        <nav
          aria-label="Settings sections"
          style={{
            display: "flex",
            flexDirection: isMobile ? "row" : "column",
            flexWrap: isMobile ? "wrap" : "nowrap",
            gap: 3,
            position: isMobile ? "static" : "sticky",
            top: 32,
            alignSelf: "flex-start",
          }}
        >
          {sections.map(({ id, label }) => {
            const Icon = ICONS[id];
            const isActive = active === id;
            return (
              <button
                key={id}
                type="button"
                onClick={() => setActive(id)}
                aria-current={isActive ? "page" : undefined}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "10px 14px",
                  borderRadius: 11,
                  cursor: "pointer",
                  fontFamily: "inherit",
                  background: isActive ? "rgba(255,213,0,.10)" : "transparent",
                  border: isActive
                    ? "1px solid rgba(255,213,0,.20)"
                    : "1px solid transparent",
                  color: isActive ? "var(--ql-yellow)" : "var(--ql-fg-muted)",
                  fontSize: 13,
                  fontWeight: 600,
                  textAlign: "left",
                  transition: "all .15s var(--ql-ease)",
                }}
              >
                <Icon size={15} strokeWidth={1.75} />
                {label}
              </button>
            );
          })}
        </nav>

        <div style={{ minWidth: 0 }}>
          {active === "brand" && (
            <BrandSection info={info} onEditBrand={onEditBrand} channelsEnabled={channelsEnabled ?? true} />
          )}
          {active === "posting" && (
            <>
              {/* Buffer schedules VIDEOS too, so the card stays for a
                  videos-only brand; only the post cards need posts. */}
              <SchedulingCard />
              {postsOn && (
                <>
                  <PostCaptionCard pipeline="beta" label="AI image" />
                  <PostCaptionCard pipeline="legacy" label="Legacy" />
                  <CtaSlidesCard />
                </>
              )}
            </>
          )}
          {active === "autopilot" && <AutopilotSection />}
          {active === "ai" && <AiSection />}
          {active === "security" && <SecuritySection />}
        </div>
      </div>

      {operatorPanel && (
        <div style={{ marginTop: 32, fontSize: 12, color: "var(--ql-fg-subtle)" }}>
          Server controls for the operator: <a href="#/operator" style={{ color: "inherit" }}>Operator</a>
        </div>
      )}
    </div>
  );
}

// ---------- BRAND ---------------------------------------------------------

/**
 * The brand itself lives in the edit wizard (name, logo, colours, fonts,
 * captions, speaker card). This section is the three things that are NOT a
 * wizard step: the way in, the brand focus, and the archived list.
 */
function BrandSection({
  info,
  onEditBrand,
  channelsEnabled,
}: {
  info: SettingsInfo | null;
  onEditBrand?: (slug: string) => void;
  /** When the server runs with CHANNELS_ENABLED=false, /api/channels 404s —
   *  the section degrades to just the brand name, no fetch, no error. */
  channelsEnabled: boolean;
}): React.JSX.Element {
  const [active, setActive] = useState<Channel | null>(null);
  const [archived, setArchived] = useState<Channel[]>([]);
  const [focus, setFocus] = useState("");
  const [saved, setSaved] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    const [live, arch] = await Promise.all([listChannelsApi(), listChannelsApi({ archived: true })]);
    const cur = live.channels.find((c) => c.slug === live.activeSlug) ?? null;
    setActive(cur);
    setFocus(cur?.voicePromptFragment ?? "");
    setArchived(arch.channels);
  }, []);
  useEffect(() => {
    if (!channelsEnabled) return;
    void load().catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, [load, channelsEnabled]);

  const saveFocus = async (): Promise<void> => {
    if (!active) return;
    setSaved("saving");
    setError(null);
    try {
      // Always name the field: an empty string CLEARS the focus. Leaving the
      // key out would leave the stored value untouched (the server only
      // applies the keys a patch names).
      await updateChannelApi(active.slug, { voicePromptFragment: focus.trim() });
      setSaved("saved");
    } catch (e) {
      setSaved("error");
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const restore = (slug: string): void => {
    setError(null);
    void restoreChannelApi(slug)
      // The brand switcher in the sidebar only re-reads the brand list on a
      // reload — the same pattern archive and the wizard use. The hash stays
      // #/settings, so this lands back on Brand with the list updated.
      .then(() => window.location.reload())
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  };

  return (
    <>
      <SettingCard title="Brand" sub="Name, logo, colours, fonts, captions and speaker card live in the brand editor.">
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div style={{ fontSize: 16, fontWeight: 600, flex: 1 }}>
            {active?.displayName ?? info?.brand.name ?? "—"}
          </div>
          {channelsEnabled && active && onEditBrand && (
            <button type="button" style={ctaSmallStyle} onClick={() => onEditBrand(active.slug)}>
              Edit brand
            </button>
          )}
        </div>
      </SettingCard>

      {channelsEnabled && (
        <SettingCard
          title="Brand focus"
          sub="What this brand is about — steers which scenes we pick and how we judge translations. Optional."
        >
          <textarea
            value={focus}
            onChange={(e) => {
              setFocus(e.target.value);
              setSaved("idle");
            }}
            rows={4}
            disabled={!active}
            style={{ ...v2Input, width: "100%", resize: "vertical" }}
            placeholder="e.g. Founders, decisions, the inner life of building a company."
          />
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 10 }}>
            <button
              type="button"
              style={ctaSmallStyle}
              disabled={!active || saved === "saving"}
              onClick={() => void saveFocus()}
            >
              {saved === "saving" ? "Saving…" : "Save"}
            </button>
            {saved === "saved" && <span style={{ fontSize: 12, color: "var(--ql-fg-muted)" }}>Saved</span>}
          </div>
        </SettingCard>
      )}

      {channelsEnabled && archived.length > 0 && (
        <SettingCard title="Archived brands" sub="Everything is kept. Restore brings a brand back to the list.">
          {archived.map((c) => (
            <div key={c.slug} style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 0" }}>
              <span style={{ width: 10, height: 10, borderRadius: "50%", background: c.brand.accent, flexShrink: 0 }} />
              <span style={{ flex: 1 }}>{c.displayName}</span>
              <button type="button" style={ctaSmallStyle} onClick={() => restore(c.slug)}>
                Restore
              </button>
            </div>
          ))}
        </SettingCard>
      )}
      {error && (
        <div role="alert" style={{ color: "#ff7373", fontSize: 13 }}>
          {error}
        </div>
      )}
    </>
  );
}

// ---------- DEFAULT POST CAPTION (editable, per pipeline) -----------------

/**
 * Editable default caption for a post pipeline's Publish-to-Buffer modal.
 * Loads the current default (operator override or bundled), lets the operator
 * edit + Save (persisted under data/ so it survives a redeploy) or Reset to
 * the bundled default. New posts pre-fill with whatever is saved here.
 */
function PostCaptionCard({
  pipeline,
  label,
}: {
  pipeline: PostPipelineId;
  label: string;
}): React.JSX.Element {
  const [caption, setCaption] = useState("");
  const [original, setOriginal] = useState("");
  const [source, setSource] = useState<"custom" | "default">("default");
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: "ok" | "err"; text: string } | null>(null);
  const [confirmResetOpen, setConfirmResetOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getDefaultCaption(pipeline)
      .then((d) => {
        if (cancelled) return;
        setCaption(d.template);
        setOriginal(d.template);
        setSource(d.source);
        setLoaded(true);
      })
      .catch((e) => {
        if (cancelled) return;
        setMsg({ kind: "err", text: e instanceof Error ? e.message : String(e) });
        setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [pipeline]);

  const dirty = caption !== original;

  const handleSave = async (): Promise<void> => {
    if (!caption.trim() || busy) return;
    setBusy(true);
    setMsg(null);
    try {
      const d = await setDefaultCaption(pipeline, caption);
      setCaption(d.template);
      setOriginal(d.template);
      setSource(d.source);
      setMsg({ kind: "ok", text: "Saved — new posts pre-fill with this caption." });
    } catch (e) {
      setMsg({ kind: "err", text: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(false);
    }
  };

  const confirmReset = async (): Promise<void> => {
    setConfirmResetOpen(false);
    setBusy(true);
    setMsg(null);
    try {
      const d = await resetDefaultCaption(pipeline);
      setCaption(d.template);
      setOriginal(d.template);
      setSource(d.source);
      setMsg({ kind: "ok", text: "Reset to the bundled default." });
    } catch (e) {
      setMsg({ kind: "err", text: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div style={{ ...cardStyle, padding: 24, marginBottom: 14 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4, flexWrap: "wrap" }}>
          <div style={{ fontSize: 14, fontWeight: 700 }}>Default caption · {label}</div>
          <PillStatus ok={source === "custom"} label="Customized" badLabel="Bundled default" />
        </div>
        <div style={{ fontSize: 12, color: "var(--ql-fg-muted)", marginBottom: 16, lineHeight: 1.55 }}>
          Pre-fills the caption in the Publish-to-Buffer modal for every{" "}
          <strong style={{ color: "var(--ql-gray-200)" }}>{label}</strong> post. Edit it once here and
          all new posts use it — you can still tweak per-post before publishing. Saved to{" "}
          <code style={{ fontFamily: "var(--ql-font-mono)" }}>data/post-caption-{pipeline}.txt</code>{" "}
          so it survives redeploys.
        </div>

        {msg && (
          <div
            role={msg.kind === "err" ? "alert" : "status"}
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 10,
              padding: "10px 12px",
              marginBottom: 14,
              background: msg.kind === "err" ? "rgba(248,113,113,.10)" : "rgba(74,222,128,.10)",
              border: `1px solid ${msg.kind === "err" ? "rgba(248,113,113,.35)" : "rgba(74,222,128,.30)"}`,
              borderRadius: 8,
              color: msg.kind === "err" ? "#FCA5A5" : "#86EFAC",
              fontSize: 12,
            }}
          >
            {msg.kind === "err" ? <AlertTriangle size={14} strokeWidth={2} /> : <Check size={14} strokeWidth={2.5} />}
            <span>{msg.text}</span>
          </div>
        )}

        <textarea
          dir="auto"
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
          disabled={!loaded || busy}
          rows={8}
          placeholder={loaded ? "" : "Loading…"}
          spellCheck={false}
          style={{
            width: "100%",
            fontFamily: "var(--ql-font-mono)",
            fontSize: 12,
            padding: 12,
            background: "rgba(0,0,0,.30)",
            border: "1px solid rgba(255,255,255,.08)",
            borderRadius: 8,
            color: "var(--ql-fg)",
            resize: "vertical",
            outline: "none",
            lineHeight: 1.6,
            boxSizing: "border-box",
            minHeight: 160,
          }}
        />

        <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 12, flexWrap: "wrap" }}>
          <button
            type="button"
            onClick={() => void handleSave()}
            disabled={busy || !dirty || !caption.trim()}
            style={{
              ...ctaSmallStyle,
              cursor: busy || !dirty || !caption.trim() ? "not-allowed" : "pointer",
              opacity: busy || !dirty || !caption.trim() ? 0.5 : 1,
            }}
          >
            {busy ? "Saving…" : "Save default"}
          </button>
          {source === "custom" && (
            <button
              type="button"
              onClick={() => setConfirmResetOpen(true)}
              disabled={busy}
              style={{
                ...ctaSmallStyle,
                background: "rgba(255,255,255,.04)",
                color: "var(--ql-gray-200)",
                border: "1px solid rgba(255,255,255,.10)",
                boxShadow: "none",
                cursor: busy ? "not-allowed" : "pointer",
                opacity: busy ? 0.5 : 1,
              }}
            >
              <Trash2 size={13} strokeWidth={2} />
              Reset to default
            </button>
          )}
          {dirty && (
            <span style={{ fontSize: 11, color: "#FBBF24" }}>Unsaved changes</span>
          )}
        </div>
      </div>
      <ConfirmDialog
        open={confirmResetOpen}
        title={`Reset the ${label} caption?`}
        message="Drops your saved caption and reverts to the bundled default. You can edit and save a new one any time."
        confirmLabel="Reset to default"
        danger
        busy={busy}
        onConfirm={() => void confirmReset()}
        onCancel={() => setConfirmResetOpen(false)}
      />
    </>
  );
}

// ---------- CTA SLIDES (legacy publish appendix) ---------------------------

function fileToDataUrl(f: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = () => reject(new Error(`could not read ${f.name}`));
    r.readAsDataURL(f);
  });
}

/**
 * CTA slides library — promo slides appended automatically to every
 * published Legacy post (round-robin across the enabled CTAs) on the
 * ticked platforms. Fully automatic once the master switch is on; the
 * Publish modal offers a per-post opt-out.
 */
function CtaSlidesCard(): React.JSX.Element {
  const [cfg, setCfg] = useState<CtaConfig | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: "ok" | "err"; text: string } | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [newLabel, setNewLabel] = useState("");
  const [newFiles, setNewFiles] = useState<File[]>([]);
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    getCtaConfig()
      .then(setCfg)
      .catch((e) => setMsg({ kind: "err", text: e instanceof Error ? e.message : String(e) }));
  }, []);

  const patch = async (p: Parameters<typeof updateCtaConfig>[0]): Promise<void> => {
    if (busy) return;
    setBusy(true);
    setMsg(null);
    try {
      setCfg(await updateCtaConfig(p));
    } catch (e) {
      setMsg({ kind: "err", text: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(false);
    }
  };

  const handleAdd = async (): Promise<void> => {
    if (adding || !newLabel.trim() || newFiles.length < 1 || newFiles.length > 2) return;
    setAdding(true);
    setMsg(null);
    try {
      const images = await Promise.all(
        newFiles.map(async (f) => ({ filename: f.name, dataUrl: await fileToDataUrl(f) })),
      );
      await createCta(newLabel.trim(), images);
      setNewLabel("");
      setNewFiles([]);
      setCfg(await getCtaConfig());
      setMsg({ kind: "ok", text: "CTA added to the rotation." });
    } catch (e) {
      setMsg({ kind: "err", text: e instanceof Error ? e.message : String(e) });
    } finally {
      setAdding(false);
    }
  };

  const confirmDelete = async (): Promise<void> => {
    if (!confirmDeleteId) return;
    setConfirmDeleteId(null);
    setBusy(true);
    setMsg(null);
    try {
      await deleteCta(confirmDeleteId);
      setCfg(await getCtaConfig());
      setMsg({ kind: "ok", text: "CTA removed." });
    } catch (e) {
      setMsg({ kind: "err", text: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(false);
    }
  };

  if (!cfg) return <LoadingCard />;

  const services = [
    ["instagram", "Instagram"],
    ["linkedin", "LinkedIn"],
    ["twitter", "Twitter / X"],
  ] as const;

  return (
    <>
      <SettingCard
        title="CTA slides · Legacy posts"
        sub={
          <>
            Promo slides appended automatically to every published Legacy post — the rotation
            cycles through the enabled CTAs so followers see a different one each time. Twitter
            fits only 1-slide CTAs (4-image cap); over-cap posts go out plain there.
          </>
        }
      >
        {/* Master switch */}
        <div
          style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "14px 16px", borderRadius: 14, marginBottom: 12,
            background: cfg.enabled ? "rgba(255,213,0,.04)" : "rgba(255,255,255,.02)",
            border: cfg.enabled ? "1px solid rgba(255,213,0,.20)" : "1px solid rgba(255,255,255,.06)",
            transition: "all .15s var(--ql-ease)",
          }}
        >
          <div>
            <div style={{ fontSize: 14, fontWeight: 600, color: "var(--ql-fg)" }}>
              Append CTA slides on publish
            </div>
            <div style={{ fontSize: 12, color: "var(--ql-fg-muted)", marginTop: 2 }}>
              {cfg.enabled
                ? "On — the next publish appends the CTA marked “Next” below."
                : "Off — posts publish exactly as before (3 slides)."}
            </div>
          </div>
          <PillToggle on={cfg.enabled} disabled={busy} onChange={(next) => void patch({ enabled: next })} />
        </div>

        {/* Platform ticks */}
        <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 16 }}>
          {services.map(([svc, label]) => (
            <label
              key={svc}
              style={{
                display: "flex", alignItems: "center", gap: 8, fontSize: 13,
                color: "var(--ql-fg)", cursor: busy ? "wait" : "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={cfg.platforms[svc]}
                disabled={busy}
                onChange={(e) => void patch({ platforms: { [svc]: e.target.checked } })}
              />
              {label}
            </label>
          ))}
        </div>

        {/* CTA rotation list */}
        {cfg.ctas.length === 0 ? (
          <div style={{ fontSize: 12, color: "var(--ql-fg-muted)", marginBottom: 16 }}>
            No CTAs yet — add one below.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
            {cfg.ctas.map((cta) => {
              const isNext = cfg.next?.id === cta.id;
              return (
                <div
                  key={cta.id}
                  style={{
                    display: "flex", alignItems: "center", gap: 12,
                    padding: "10px 12px", borderRadius: 12,
                    background: "rgba(255,255,255,.02)",
                    border: isNext ? "1px solid rgba(255,213,0,.25)" : "1px solid rgba(255,255,255,.06)",
                    opacity: cta.enabled ? 1 : 0.5,
                  }}
                >
                  <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
                    {cta.slides.map((s) => (
                      <img
                        key={s}
                        src={ctaSlideUrl(cta.id, s)}
                        alt=""
                        loading="lazy"
                        style={{
                          width: 44, height: 55, objectFit: "cover", borderRadius: 6,
                          border: "1px solid rgba(255,255,255,.10)",
                        }}
                      />
                    ))}
                  </div>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "var(--ql-fg)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {cta.label}
                    </div>
                    <div style={{ fontSize: 11, color: "var(--ql-fg-muted)", marginTop: 2 }}>
                      {cta.slides.length} slide{cta.slides.length > 1 ? "s" : ""}
                      {isNext && (
                        <span style={{ marginLeft: 8, color: "var(--ql-yellow)", fontWeight: 700 }}>
                          Next up
                        </span>
                      )}
                    </div>
                  </div>
                  <PillToggle
                    on={cta.enabled}
                    disabled={busy}
                    onChange={(next) => void patch({ ctas: [{ id: cta.id, enabled: next }] })}
                  />
                  <button
                    type="button"
                    onClick={() => setConfirmDeleteId(cta.id)}
                    disabled={busy}
                    title="Delete this CTA"
                    style={{
                      background: "transparent", border: "none", cursor: "pointer",
                      color: "var(--ql-fg-muted)", padding: 6, display: "flex",
                    }}
                  >
                    <Trash2 size={14} strokeWidth={2} />
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {/* Add CTA */}
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
          <input
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            placeholder="New CTA label (e.g. Referral $5)"
            style={{ ...inputStyle, flex: "1 1 180px", minWidth: 0 }}
          />
          <label style={{ ...ctaSmallStyle, cursor: "pointer" }}>
            {newFiles.length > 0
              ? `${newFiles.length} image${newFiles.length > 1 ? "s" : ""} picked`
              : "Pick 1–2 images"}
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp"
              multiple
              onChange={(e) => setNewFiles(Array.from(e.target.files ?? []).slice(0, 2))}
              style={{ display: "none" }}
            />
          </label>
          <button
            type="button"
            onClick={() => void handleAdd()}
            disabled={adding || !newLabel.trim() || newFiles.length === 0}
            style={{
              ...ctaSmallStyle,
              opacity: adding || !newLabel.trim() || newFiles.length === 0 ? 0.5 : 1,
            }}
          >
            {adding ? "Adding…" : "Add CTA"}
          </button>
        </div>

        {msg && (
          <div
            style={{
              marginTop: 10, fontSize: 12,
              color: msg.kind === "ok" ? "#7BD389" : "var(--ql-danger)",
            }}
          >
            {msg.text}
          </div>
        )}
      </SettingCard>
      <ConfirmDialog
        open={confirmDeleteId !== null}
        title="Delete this CTA?"
        message="Removes it from the rotation and deletes its slide images. Posts already published keep their slides."
        confirmLabel="Delete CTA"
        danger
        busy={busy}
        onConfirm={() => void confirmDelete()}
        onCancel={() => setConfirmDeleteId(null)}
      />
    </>
  );
}

// ---------- AI & CREDITS --------------------------------------------------

/**
 * The ONE AI section: whose key pays, which model the design agent uses, the
 * credit pot. The provider card owns all of it; the old "other API keys"
 * status rows (with their inert pencil) were server facts, not settings.
 */
function AiSection(): React.JSX.Element {
  return <AiProviderCard />;
}

// ===========================================================================
// SchedulingCard — Buffer connect/disconnect/channel listing, byte-stable.
// ===========================================================================

function SchedulingCard(): React.JSX.Element {
  const [status, setStatus] = useState<SchedulingStatus | null>(null);
  const [channels, setChannels] = useState<SchedulingChannel[] | null>(null);
  const [token, setToken] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: "ok" | "err"; text: string } | null>(null);
  const [confirmDisconnectOpen, setConfirmDisconnectOpen] = useState(false);
  // Which channel this card is connecting. getSchedulingStatus/connectScheduling
  // already operate on the ACTIVE channel's per-channel creds file; this label
  // just makes it visible which brand you're editing. Only shown when >1 channel
  // exists, so single-channel V1 looks identical.
  const [activeChannel, setActiveChannel] = useState<{ displayName: string } | null>(null);
  const [channelCount, setChannelCount] = useState(1);

  useEffect(() => {
    listChannelsApi()
      .then(({ activeSlug, channels }) => {
        setChannelCount(channels.length);
        const c = channels.find((ch) => ch.slug === activeSlug);
        if (c) setActiveChannel({ displayName: c.displayName });
      })
      .catch(() => {/* channels off or unavailable — no label */});
  }, []);

  const refresh = useCallback(async (): Promise<void> => {
    try {
      const s = await getSchedulingStatus();
      setStatus(s);
      if (s.connected) {
        try {
          setChannels(await listSchedulingChannels());
        } catch (err) {
          setChannels(null);
          setMsg({
            kind: "err",
            text: `Couldn't list channels: ${err instanceof Error ? err.message : String(err)}`,
          });
        }
      } else {
        setChannels(null);
      }
    } catch {
      /* non-fatal */
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const handleConnect = async (): Promise<void> => {
    if (!token.trim()) return;
    setBusy(true);
    setMsg(null);
    try {
      const next = await connectScheduling(token.trim());
      setStatus(next);
      setToken("");
      setMsg({
        kind: "ok",
        text: `Connected as ${next.accountName ?? "your Buffer account"}.`,
      });
      try {
        setChannels(await listSchedulingChannels());
      } catch {
        /* surfaced via msg above */
      }
    } catch (err) {
      setMsg({ kind: "err", text: err instanceof Error ? err.message : String(err) });
    } finally {
      setBusy(false);
    }
  };

  const handleDisconnect = (): void => setConfirmDisconnectOpen(true);

  const confirmDisconnect = async (): Promise<void> => {
    setConfirmDisconnectOpen(false);
    setBusy(true);
    setMsg(null);
    try {
      const next = await disconnectScheduling();
      setStatus(next);
      setChannels(null);
      setMsg({ kind: "ok", text: "Disconnected." });
    } catch (err) {
      setMsg({ kind: "err", text: err instanceof Error ? err.message : String(err) });
    } finally {
      setBusy(false);
    }
  };

  const connected = status?.connected ?? false;

  return (
    <>
      <div style={{ ...cardStyle, padding: 24, marginBottom: 14 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 4, flexWrap: "wrap" }}>
          <div style={{ fontSize: 14, fontWeight: 700 }}>Post scheduling · Buffer</div>
          {channelCount > 1 && activeChannel && (
            <span
              title="Buffer connects the currently-active channel. Switch channels from the top-right picker to connect another brand."
              style={{
                fontSize: 11, fontWeight: 700, letterSpacing: ".02em",
                padding: "3px 10px", borderRadius: 999,
                color: "var(--ql-yellow)", background: "rgba(255,213,0,.10)",
                border: "1px solid rgba(255,213,0,.28)",
              }}
            >
              Buffer for: {activeChannel.displayName}
            </span>
          )}
        </div>
        <div
          style={{
            fontSize: 12,
            color: "var(--ql-fg-muted)",
            marginBottom: 16,
            lineHeight: 1.55,
          }}
        >
          Once connected, every <strong style={{ color: "var(--ql-gray-200)" }}>ready</strong>{" "}
          render gets a "Schedule post" button on the Job detail page — pick a date/time, choose
          channels, and the rendered mp4 is queued in Buffer with per-platform captions (Pinterest
          auto-shortened to 500 chars, YouTube gets a title from the first line).
          <br />
          <br />
          Get your token at{" "}
          <a
            href="https://publish.buffer.com/account/apps"
            target="_blank"
            rel="noreferrer"
            style={{ color: "var(--ql-yellow)" }}
          >
            publish.buffer.com → Account → API
          </a>
          . Persists in{" "}
          <code style={{ fontFamily: "var(--ql-font-mono)" }}>data/scheduling-credentials.json</code>{" "}
          (chmod 600).
        </div>

        {msg && (
          <div
            role={msg.kind === "err" ? "alert" : "status"}
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 10,
              padding: "10px 12px",
              marginBottom: 14,
              background: msg.kind === "err" ? "rgba(248,113,113,.10)" : "rgba(74,222,128,.10)",
              border: `1px solid ${msg.kind === "err" ? "rgba(248,113,113,.35)" : "rgba(74,222,128,.30)"}`,
              borderRadius: 8,
              color: msg.kind === "err" ? "#FCA5A5" : "#86EFAC",
              fontSize: 12,
            }}
          >
            {msg.kind === "err" ? (
              <AlertTriangle size={14} strokeWidth={2} />
            ) : (
              <Check size={14} strokeWidth={2.5} />
            )}
            <span>{msg.text}</span>
          </div>
        )}

        <div
          style={{
            display: "flex",
            gap: 12,
            alignItems: "center",
            marginBottom: connected ? 14 : 12,
            flexWrap: "wrap",
          }}
        >
          <PillStatus
            ok={connected}
            label={connected ? `Connected · ${status?.accountName ?? "Buffer"}` : "Connect"}
            badLabel="Not connected"
          />
          {connected && status?.savedAt && (
            <KV label="Saved" value={formatRelativeTime(new Date(status.savedAt).getTime())} />
          )}
          {connected && channels && <KV label="Channels" value={String(channels.length)} />}
        </div>

        {!connected ? (
          <>
            <input
              type="password"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder="Paste your Buffer personal API token"
              autoComplete="off"
              spellCheck={false}
              style={{
                width: "100%",
                fontFamily: "var(--ql-font-mono)",
                fontSize: 12,
                padding: "10px 12px",
                background: "rgba(0,0,0,.30)",
                border: "1px solid rgba(255,255,255,.08)",
                borderRadius: 8,
                color: "var(--ql-fg)",
                outline: "none",
                marginBottom: 12,
              }}
            />
            <button
              type="button"
              onClick={() => void handleConnect()}
              disabled={busy || !token.trim()}
              style={{
                ...ctaSmallStyle,
                cursor: busy || !token.trim() ? "not-allowed" : "pointer",
                opacity: busy || !token.trim() ? 0.5 : 1,
              }}
            >
              {busy ? "Validating…" : "Connect Buffer"}
            </button>
          </>
        ) : (
          <>
            {channels && (
              <div style={{ marginBottom: 14 }}>
                <div
                  style={{
                    fontSize: 11,
                    color: "var(--ql-fg-muted)",
                    textTransform: "uppercase",
                    letterSpacing: ".06em",
                    marginBottom: 8,
                    fontWeight: 700,
                  }}
                >
                  Channels
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {channels.map((c) => (
                    <span
                      key={c.id}
                      style={{
                        fontSize: 12,
                        padding: "5px 12px 5px 6px",
                        borderRadius: 999,
                        background: "rgba(255,255,255,.04)",
                        border: "1px solid rgba(255,255,255,.06)",
                        color: "var(--ql-gray-100)",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 8,
                      }}
                      title={`${serviceLabel(c.service)} · ${c.rawService ?? c.service}`}
                    >
                      <ServiceIcon service={c.service} size={20} />
                      {c.name}
                    </span>
                  ))}
                  {channels.length === 0 && (
                    <span style={{ fontSize: 12, color: "var(--ql-fg-muted)" }}>
                      No channels yet — add a network in Buffer first.
                    </span>
                  )}
                </div>
              </div>
            )}
            <button
              type="button"
              onClick={handleDisconnect}
              disabled={busy}
              style={{
                ...ctaSmallStyle,
                background: "rgba(255,255,255,.04)",
                color: "var(--ql-gray-200)",
                border: "1px solid rgba(255,255,255,.10)",
                boxShadow: "none",
                cursor: busy ? "not-allowed" : "pointer",
                opacity: busy ? 0.5 : 1,
              }}
            >
              <Trash2 size={13} strokeWidth={2} />
              Disconnect
            </button>
          </>
        )}
      </div>

      <ConfirmDialog
        open={confirmDisconnectOpen}
        title="Disconnect Buffer?"
        message="You'll need to paste a fresh access token to reconnect. Scheduled posts in Buffer aren't affected — they keep their schedule."
        confirmLabel="Disconnect"
        danger
        busy={busy}
        onConfirm={() => void confirmDisconnect()}
        onCancel={() => setConfirmDisconnectOpen(false)}
      />
    </>
  );
}

/* ------------------------------- Security ------------------------------- */

/**
 * Two-factor (TOTP) management. Enrollment is two-step — the QR must be
 * scanned AND a live code confirmed before anything activates, so a
 * mis-scan can't lock the operator out. Recovery if the phone is lost:
 * SSH to the VPS and delete /app/data/auth/mfa.json (password-only again).
 */
function SecuritySection(): React.JSX.Element {
  const [gateEnabled, setGateEnabled] = useState<boolean | null>(null);
  const [enrolled, setEnrolled] = useState(false);
  // Pending enrollment (QR shown, waiting for the confirm code).
  const [pending, setPending] = useState<{ secret: string; uri: string } | null>(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const refresh = useCallback((): void => {
    checkAuth()
      .then((s) => {
        setGateEnabled(s.gateEnabled);
        setEnrolled(s.mfaEnrolled === true);
      })
      .catch(() => setGateEnabled(null));
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const beginSetup = async (): Promise<void> => {
    if (busy) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      setPending(await mfaSetup());
      setCode("");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const confirmEnable = async (): Promise<void> => {
    if (busy || code.trim().length !== 6) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await mfaEnable(code.trim());
      setPending(null);
      setCode("");
      setEnrolled(true); // optimistic — avoids a one-RTT flash of the wrong state
      setNotice("Two-factor is on. Your next login will ask for the password and a 6-digit code together.");
      refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const confirmDisable = async (): Promise<void> => {
    if (busy || code.trim().length !== 6) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await mfaDisable(code.trim());
      setCode("");
      setEnrolled(false); // optimistic
      setNotice("Two-factor is off — login is password-only again.");
      refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const codeInput = (
    <input
      type="text"
      inputMode="numeric"
      value={code}
      // Strip non-digits THEN cap at 6 — capping first (maxLength) would drop a
      // digit when pasting a spaced/hyphenated code like "123 456".
      onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
      placeholder="123456"
      aria-label="Authentication code"
      disabled={busy}
      style={{
        ...inputStyle,
        width: 140,
        fontSize: 16,
        letterSpacing: "0.3em",
        textAlign: "center",
        fontFamily: "var(--ql-font-mono)",
      }}
    />
  );

  return (
    <div style={{ ...cardStyle, padding: 24 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
        <Lock size={16} strokeWidth={2} style={{ color: "var(--ql-yellow)" }} />
        <span style={{ fontSize: 15, fontWeight: 700 }}>Two-factor authentication</span>
        <span
          style={{
            marginLeft: "auto",
            fontSize: 11,
            fontWeight: 700,
            padding: "3px 10px",
            borderRadius: 999,
            background: enrolled ? "rgba(74,222,128,.15)" : "rgba(255,255,255,.06)",
            color: enrolled ? "var(--ql-success)" : "var(--ql-fg-muted)",
          }}
        >
          {enrolled ? "ON" : "OFF"}
        </span>
      </div>
      <div style={{ fontSize: 13, color: "var(--ql-fg-muted)", lineHeight: 1.6, marginBottom: 16 }}>
        Adds a 6-digit authenticator code on top of the password. Every login
        asks for the password and the current code together. Every route is
        already behind the session gate — the second factor hardens how that
        session is issued.
      </div>

      {gateEnabled === false && (
        <div style={{ fontSize: 13, color: "var(--ql-warning, #FBBF24)", lineHeight: 1.6 }}>
          Password auth is not configured on this server — run
          {" "}<code>npx tsx scripts/setup-auth.ts</code> first, then enable
          two-factor here.
        </div>
      )}

      {error && (
        <div
          role="alert"
          style={{
            marginBottom: 12,
            padding: "9px 12px",
            borderRadius: 10,
            border: "1px solid rgba(248,113,113,.30)",
            background: "rgba(239,68,68,.08)",
            color: "#FCA5A5",
            fontSize: 13,
          }}
        >
          {error}
        </div>
      )}
      {notice && (
        <div
          style={{
            marginBottom: 12,
            padding: "9px 12px",
            borderRadius: 10,
            border: "1px solid rgba(74,222,128,.30)",
            background: "rgba(74,222,128,.08)",
            color: "var(--ql-success)",
            fontSize: 13,
          }}
        >
          {notice}
        </div>
      )}

      {gateEnabled && !enrolled && !pending && (
        <button
          type="button"
          onClick={() => void beginSetup()}
          disabled={busy}
          style={{ ...ctaSmallStyle, cursor: busy ? "not-allowed" : "pointer", opacity: busy ? 0.5 : 1 }}
        >
          <Lock size={14} strokeWidth={2} />
          {busy ? "Preparing…" : "Enable two-factor"}
        </button>
      )}

      {gateEnabled && !enrolled && pending && (
        <div>
          <div style={{ fontSize: 13, color: "var(--ql-fg)", marginBottom: 12 }}>
            1 · Scan this QR with your authenticator app (Google Authenticator,
            1Password, Authy…):
          </div>
          <div
            // White padding box: QR needs a light background + quiet zone to scan.
            // uqr emits an aspect-only SVG (no width/height) → pin it to 192px so
            // it can't fall back to ~300px and overflow a narrow card at 380px.
            style={{
              display: "inline-block",
              padding: 12,
              background: "#fff",
              borderRadius: 12,
              marginBottom: 10,
              lineHeight: 0,
            }}
            dangerouslySetInnerHTML={{
              __html: renderSVG(pending.uri).replace("<svg ", '<svg width="192" height="192" '),
            }}
          />
          <div style={{ fontSize: 11, color: "var(--ql-fg-subtle)", marginBottom: 16 }}>
            Can't scan? Enter the secret manually:{" "}
            <code style={{ userSelect: "all", fontSize: 11 }}>{pending.secret}</code>
          </div>
          <div style={{ fontSize: 13, color: "var(--ql-fg)", marginBottom: 8 }}>
            2 · Enter the 6-digit code the app shows, to confirm:
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            {codeInput}
            <button
              type="button"
              onClick={() => void confirmEnable()}
              disabled={busy || code.trim().length !== 6}
              style={{
                ...ctaSmallStyle,
                cursor: busy || code.trim().length !== 6 ? "not-allowed" : "pointer",
                opacity: busy || code.trim().length !== 6 ? 0.5 : 1,
              }}
            >
              {busy ? "Verifying…" : "Confirm & turn on"}
            </button>
            <button
              type="button"
              onClick={() => { setPending(null); setCode(""); setError(null); }}
              disabled={busy}
              style={{
                padding: "8px 12px",
                borderRadius: 10,
                border: "1px solid rgba(255,255,255,.15)",
                background: "transparent",
                color: "var(--ql-fg-muted)",
                fontFamily: "inherit",
                fontSize: 12,
                fontWeight: 600,
                cursor: busy ? "not-allowed" : "pointer",
              }}
            >
              Cancel
            </button>
          </div>
          <div style={{ fontSize: 11, color: "var(--ql-fg-subtle)", marginTop: 12, lineHeight: 1.5 }}>
            Nothing activates until the code is confirmed — a mis-scan can't
            lock you out.
          </div>
        </div>
      )}

      {gateEnabled && enrolled && (
        <div>
          <div style={{ fontSize: 13, color: "var(--ql-fg)", marginBottom: 8 }}>
            Turn off two-factor (requires a current code):
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            {codeInput}
            <button
              type="button"
              onClick={() => void confirmDisable()}
              disabled={busy || code.trim().length !== 6}
              style={{
                padding: "8px 14px",
                borderRadius: 10,
                border: "1px solid rgba(248,113,113,.40)",
                background: "rgba(239,68,68,.10)",
                color: "#FCA5A5",
                fontFamily: "inherit",
                fontSize: 12,
                fontWeight: 700,
                cursor: busy || code.trim().length !== 6 ? "not-allowed" : "pointer",
                opacity: busy || code.trim().length !== 6 ? 0.5 : 1,
              }}
            >
              {busy ? "Verifying…" : "Turn off two-factor"}
            </button>
          </div>
          <div style={{ fontSize: 11, color: "var(--ql-fg-subtle)", marginTop: 12, lineHeight: 1.5 }}>
            Lost your device? Contact us and we will reset two-factor for you.
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Autopilot — autonomous daily scan→render. Per-channel switch + per-day
 * count; everything else (source rotation, confidence bar, quotas, yielding
 * to manual renders) is server policy. Videos land in the Library as normal
 * drafts — nothing publishes.
 */
function AutopilotSection(): React.JSX.Element {
  const [status, setStatus] = useState<AutopilotStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void getAutopilot()
      .then((s) => { if (!cancelled) setStatus(s); })
      .catch((e: unknown) => { if (!cancelled) setMsg(e instanceof Error ? e.message : String(e)); });
    return () => { cancelled = true; };
  }, []);

  const patch = (slug: string, p: { enabled?: boolean; perDay?: number }): void => {
    setBusy(true);
    setMsg(null);
    void updateAutopilot({ [slug]: p })
      .then((s) => setStatus(s))
      .catch((e: unknown) => setMsg(e instanceof Error ? e.message : String(e)))
      .finally(() => setBusy(false));
  };

  if (!status) {
    // A failed initial fetch must not wedge on the spinner — show the error.
    if (msg) {
      return (
        <SettingCard title="Autopilot" sub={<span style={{ color: "#ff8b8b" }}>{msg}</span>}>
          <button
            style={ctaSmallStyle}
            onClick={() => {
              setMsg(null);
              void getAutopilot()
                .then((s) => setStatus(s))
                .catch((e: unknown) => setMsg(e instanceof Error ? e.message : String(e)));
            }}
          >
            Retry
          </button>
        </SettingCard>
      );
    }
    return <LoadingCard />;
  }

  const kindColor: Record<string, string> = {
    render: "#7ee29a",
    scan: "var(--ql-gold, #FFD500)",
    skip: "var(--ql-fg-muted)",
    error: "#ff8b8b",
  };

  return (
    <>
      <SettingCard
        title="Autopilot"
        sub={
          <>
            Once a brand is on, the server scans your existing sources (bookmarked
            videos first), picks only scenes it is confident in, with a clean ending,
            and renders up to the daily count — one at a time, always
            yielding to renders you start yourself. Finished videos wait in the
            Library for your review; nothing publishes. Day boundary: {status.timezone}.
            {" "}<a href="#/guide/autopilot" style={{ color: "var(--ql-yellow)" }}>How Autopilot decides</a>.
            {status.killSwitchOn && (
              <div style={{ color: "#ff8b8b", marginTop: 8 }}>
                QL_AUTOPILOT=off on the server — the cron is disabled; these switches
                will have no effect until it is re-enabled.
              </div>
            )}
          </>
        }
      >
        {status.channels.map((ch) => (
          <div
            key={ch.slug}
            style={{
              display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
              padding: "12px 0", borderTop: "1px solid rgba(255,255,255,.06)",
            }}
          >
            <div style={{ minWidth: 140, flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: "var(--ql-fg)" }}>{ch.displayName}</div>
              <div style={{ fontSize: 11.5, color: "var(--ql-fg-muted)", marginTop: 2 }}>
                {!ch.eligible
                  ? "needs the autoScan + videos channel features"
                  : ch.enabled
                    ? `today: ${ch.today.rendered}/${ch.perDay} rendered · ${ch.today.scans} scans` +
                      (ch.today.standingDown ? " · standing down (failures)" : ch.today.failures ? ` · ${ch.today.failures} failed` : "")
                    : "off"}
              </div>
            </div>
            <Select
              value={String(ch.perDay)}
              disabled={busy || !ch.enabled}
              onChange={(v) => patch(ch.slug, { perDay: Number(v) })}
              aria-label={`Videos per day for ${ch.displayName}`}
              size="sm"
              style={{ width: 120 }}
              options={Array.from({ length: status.maxPerDay }, (_, i) => i + 1).map((n) => ({
                value: String(n),
                label: `${n} / day`,
              }))}
            />
            <PillToggle
              on={ch.enabled}
              // Switching OFF must always be possible — a channel that lost
              // its autoScan/videos feature while enabled would otherwise
              // show a disabled toggle stuck ON.
              disabled={busy || (!ch.eligible && !ch.enabled)}
              onChange={(next) => patch(ch.slug, { enabled: next })}
            />
          </div>
        ))}
        {msg && (
          <div style={{ marginTop: 10, fontSize: 12, color: "#ff8b8b" }}>{msg}</div>
        )}
      </SettingCard>

      <SettingCard title="Recent activity" sub="What autopilot did lately — newest first.">
        {status.log.length === 0 ? (
          <div style={{ fontSize: 12.5, color: "var(--ql-fg-muted)" }}>
            Nothing yet — flip a channel on and the next tick takes it from there.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {status.log.map((l, i) => (
              <div key={i} style={{ display: "flex", gap: 10, fontSize: 12, lineHeight: 1.5 }}>
                <span style={{ color: "var(--ql-fg-muted)", whiteSpace: "nowrap" }}>
                  {new Date(l.at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                </span>
                <span style={{ color: "var(--ql-fg-muted)" }}>{l.channelSlug}</span>
                <span style={{ color: kindColor[l.kind] ?? "var(--ql-fg)" }}>{l.detail}</span>
              </div>
            ))}
          </div>
        )}
      </SettingCard>
    </>
  );
}
